import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  LinearProgress,
  Avatar,
  Button,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { toast } from "react-toastify";
import { resolveAuthToken, uploadImageFile } from "../../Utils/uploadImageFile";
import { apiFlagIsTrue } from "../../Utils/func";
import {
  PageHeader,
  Card,
  DataTable,
  StatusBadge,
  ActionButton,
} from "../shared";

const readMasterOrgField = (target, key) =>
  target?.[key] ?? target?.master_org?.[key];

/** Default permissions for new club partners (club partners) created in admin. */
const DEFAULT_MASTER_ORG_PERMISSIONS = {
  allow_member_emails: false,
  can_advertise: true,
  can_send_alerts: false,
  can_invite_clubs: true,
};

const readPermissionFlag = (target, key, defaultValue) => {
  const raw = readMasterOrgField(target, key);
  if (raw == null) return defaultValue;
  return apiFlagIsTrue(raw);
};

const getEditInitialValues = (target) => {
  const canInviteRaw = readMasterOrgField(target, "can_invite_clubs");
  return {
    master_org_id:
      target?.id || target?.master_org_id || target?.master_org?.id || "",
    max_sub_orgs: target?.max_sub_orgs ?? "",
    status: target?.status || "active",
    allow_member_emails: readPermissionFlag(
      target,
      "allow_member_emails",
      DEFAULT_MASTER_ORG_PERMISSIONS.allow_member_emails
    ),
    can_advertise: readPermissionFlag(
      target,
      "can_advertise",
      DEFAULT_MASTER_ORG_PERMISSIONS.can_advertise
    ),
    can_send_alerts: readPermissionFlag(
      target,
      "can_send_alerts",
      DEFAULT_MASTER_ORG_PERMISSIONS.can_send_alerts
    ),
    can_invite_clubs:
      canInviteRaw == null
        ? DEFAULT_MASTER_ORG_PERMISSIONS.can_invite_clubs
        : apiFlagIsTrue(canInviteRaw),
  };
};

const mergeMasterOrgRow = (row, details) => {
  if (!details) return row;
  const nested = details.master_org || {};
  return { ...row, ...nested, ...details };
};

const resolveMasterOrgId = (row) =>
  row?.id ?? row?.master_org_id ?? row?.master_org?.id ?? null;

const MasterOrg = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [list, setList] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createProfileImageId, setCreateProfileImageId] = useState(null);
  const [createProfileImageUrl, setCreateProfileImageUrl] = useState("");
  const [editProfileImageId, setEditProfileImageId] = useState(null);
  const [editProfileImageUrl, setEditProfileImageUrl] = useState("");
  const [uploadingCreateImage, setUploadingCreateImage] = useState(false);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const createImageInputRef = useRef(null);
  const editImageInputRef = useRef(null);

  const profile = props?.profile || props?.props?.profile || {};
  const token = resolveAuthToken(profile);
  const userType = profile?.user_type || profile?.data?.user_type;

  const loadSports = async () => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const data = await ApiServices.PostApiCall(
      ApiEndpoint.SPORT_LIST_COMMON,
      {},
      headers
    );
    if (data?.status && Array.isArray(data.data)) {
      setSportsList(data.data);
    }
  };

  const loadMasterOrgs = async () => {
    if (!token) return;
    setLoading(true);
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    try {
      // Use the original ADMIN_MASTER_ORG_LIST endpoint to get the correct ID structure
      const data = await ApiServices.GetApiCall(
        ApiEndpoint.ADMIN_MASTER_ORG_LIST,
        headers
      );
      setLoading(false);
      if (data?.status) {
        setList(data.data || []);
      } else {
        setList([]);
        if (data?.message) {
          toast.error(data.message);
        }
      }
    } catch (error) {
      setLoading(false);
      setList([]);
      toast.error("Failed to fetch club partners");
    }
  };

  useEffect(() => {
    if (token && userType === "admin") {
      loadSports();
      loadMasterOrgs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userType]);

  const isValidImageFile = (file) => {
    if (/^image\/(jpeg|png|jpg)$/i.test(file.type)) return true;
    if (!file.type && /\.(jpe?g|png)$/i.test(file.name)) return true;
    return false;
  };

  const handleCreateImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }
    if (!isValidImageFile(file)) {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setUploadingCreateImage(true);
    try {
      const uploadResponse = await uploadImageFile(file, token);
      if (uploadResponse?.status && uploadResponse?.data?.id) {
        setCreateProfileImageId(uploadResponse.data.id);
        setCreateProfileImageUrl(
          uploadResponse.data.url || URL.createObjectURL(file)
        );
        toast.success("Image uploaded successfully");
      } else {
        toast.error(uploadResponse?.message || "Failed to upload image.");
      }
    } finally {
      setUploadingCreateImage(false);
      if (createImageInputRef.current) {
        createImageInputRef.current.value = "";
      }
    }
  };

  const handleEditImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }
    if (!isValidImageFile(file)) {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setUploadingEditImage(true);
    try {
      const uploadResponse = await uploadImageFile(file, token);
      if (uploadResponse?.status && uploadResponse?.data?.id) {
        setEditProfileImageId(uploadResponse.data.id);
        setEditProfileImageUrl(
          uploadResponse.data.url || URL.createObjectURL(file)
        );
        toast.success("Image uploaded successfully");
      } else {
        toast.error(uploadResponse?.message || "Failed to upload image.");
      }
    } finally {
      setUploadingEditImage(false);
      if (editImageInputRef.current) {
        editImageInputRef.current.value = "";
      }
    }
  };

  const createFormik = useFormik({
    initialValues: {
      email: "",
      password: "",
      organisation_name: "",
      max_sub_orgs: "",
      sport_ids: [],
      allow_member_emails: DEFAULT_MASTER_ORG_PERMISSIONS.allow_member_emails,
      can_advertise: DEFAULT_MASTER_ORG_PERMISSIONS.can_advertise,
      can_send_alerts: DEFAULT_MASTER_ORG_PERMISSIONS.can_send_alerts,
      can_invite_clubs: DEFAULT_MASTER_ORG_PERMISSIONS.can_invite_clubs,
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string().required("Password is required"),
      organisation_name: Yup.string().required("Club name is required"),
      max_sub_orgs: Yup.number()
        .typeError("Max clubs must be a number")
        .min(0, "Max clubs must be 0 or more")
        .required("Max clubs is required"),
      sport_ids: Yup.array().of(Yup.number()),
    }),
    onSubmit: async (values) => {
      if (!token) return;
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      const body = {
        ...values,
        allow_member_emails: !!values.allow_member_emails,
        can_advertise: !!values.can_advertise,
        can_send_alerts: !!values.can_send_alerts,
        can_invite_clubs: !!values.can_invite_clubs,
      };
      // Convert sport_ids array to the format expected by backend (only if provided)
      if (body.sport_ids && body.sport_ids.length > 0) {
        body.sport_ids = body.sport_ids.map(id => Number(id));
      } else {
        // Remove sport_ids if empty to avoid sending empty array
        delete body.sport_ids;
      }
      if (createProfileImageId) {
        body.id_item_logo = createProfileImageId;
      }
      props.setIsLoaded(true);
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_MASTER_ORG_CREATE,
        JSON.stringify(body),
        headers
      );
      props.setIsLoaded(false);
      if (data?.status) {
        toast.success(data.message || "Master org created");
        setOpenCreate(false);
        createFormik.resetForm();
        setCreateProfileImageId(null);
        setCreateProfileImageUrl("");
        loadMasterOrgs();
      } else {
        toast.error(data?.message || "Failed to create club partner");
      }
    },
  });

  useEffect(() => {
    if (editTarget) {
      // Get logo URL directly from API response (backend now includes logo_url)
      const logoUrl = editTarget?.logo_url || "";
      setEditProfileImageUrl(logoUrl);
      setEditProfileImageId(null);
    }
  }, [editTarget]);

  const editFormik = useFormik({
    enableReinitialize: false,
    initialValues: getEditInitialValues(null),
    validationSchema: Yup.object({
      master_org_id: Yup.number()
        .typeError("Master org id is required")
        .required("Master org id is required"),
      max_sub_orgs: Yup.number()
        .typeError("Max clubs must be a number")
        .min(0, "Max clubs must be 0 or more")
        .required("Max clubs is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!token) {
        setSubmitting(false);
        return;
      }
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };
      // Ensure master_org_id is set correctly
      // The ID from /admin/orgs/all might be the organisation_details.id
      // But the update endpoint needs the master_org.id (which might be in a different field)
      const masterOrgId = values.master_org_id || editTarget?.id || editTarget?.master_org_id || editTarget?.master_org_id || "";
      
      if (!masterOrgId) {
        toast.error("Master club ID is missing");
        setSubmitting(false);
        return;
      }
      
      // Debug: log the editTarget to see what fields are available
      
      const body = {
        master_org_id: Number(masterOrgId),
        max_sub_orgs: Number(values.max_sub_orgs),
        status: values.status,
        allow_member_emails: !!values.allow_member_emails,
        can_advertise: !!values.can_advertise,
        can_send_alerts: !!values.can_send_alerts,
        can_invite_clubs: !!values.can_invite_clubs,
      };
      
      if (editProfileImageId) {
        body.id_item_logo = editProfileImageId;
      }
      
      
      try {
        props.setIsLoaded(true);
        const data = await ApiServices.PostApiCall(
          ApiEndpoint.ADMIN_MASTER_ORG_UPDATE,
          JSON.stringify(body),
          headers
        );
        props.setIsLoaded(false);
        if (data?.status) {
          toast.success(data.message || "Master org updated");
          const savedId = Number(masterOrgId);
          setList((prev) =>
            prev.map((item) => {
              const itemId = resolveMasterOrgId(item);
              if (itemId !== savedId) return item;
              return {
                ...item,
                max_sub_orgs: body.max_sub_orgs,
                status: body.status,
                allow_member_emails: body.allow_member_emails,
                can_advertise: body.can_advertise,
                can_send_alerts: body.can_send_alerts,
                can_invite_clubs: body.can_invite_clubs,
                ...(data.data ? mergeMasterOrgRow(item, data.data) : {}),
              };
            })
          );
          setOpenEdit(false);
          setEditTarget(null);
          setEditProfileImageId(null);
          setEditProfileImageUrl("");
          loadMasterOrgs();
        } else {
          toast.error(data?.message || "Failed to update club partner");
        }
      } catch (error) {
        console.error("Error updating club partner:", error);
        toast.error("An error occurred while updating club partner");
        props.setIsLoaded(false);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const editTargetId =
    editTarget?.id ?? editTarget?.master_org_id ?? editTarget?.master_org?.id;

  useEffect(() => {
    if (!openEdit || !editTarget || editLoading) return;
    editFormik.resetForm({ values: getEditInitialValues(editTarget) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEdit, editTargetId, editLoading]);

  const handleOpenEdit = async (row) => {
    setOpenEdit(true);
    setEditLoading(true);
    setEditTarget(row);

    const masterOrgId = resolveMasterOrgId(row);
    if (!token || !masterOrgId) {
      setEditLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };

    try {
      const data = await ApiServices.PostApiCall(
        ApiEndpoint.ADMIN_MASTER_ORG_DETAILS,
        JSON.stringify({ master_org_id: Number(masterOrgId) }),
        headers
      );
      if (data?.status && data?.data) {
        setEditTarget(mergeMasterOrgRow(row, data.data));
      } else if (data?.message) {
        toast.error(data.message);
      }
    } catch {
      toast.error("Failed to load club partner details");
    } finally {
      setEditLoading(false);
    }
  };

  if (userType !== "admin") {
    return (
      <Box>
        <PageHeader title="Club Partners" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#5a6a85" }}>
            Master org users cannot access admin screens.
          </Typography>
        </Card>
      </Box>
    );
  }

  // Table columns
  const columns = [
    {
      field: "organisation_name",
      label: "Club",
      width: 320,
      render: (_, row) => {
        // Get logo URL directly from API response (backend now includes logo_url) - same pattern as Organizations page
        const logoUrl = row.logo_url || "";
        const orgName = row.organisation_name || row.name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "M"}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#2a3547", fontSize: "0.875rem" }}>
                {row.organisation_name || row.name || "-"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#7c8fac" }}>
                {row.email || "-"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "max_sub_orgs",
      label: "Capacity",
      width: 400,
      minWidth: 200,
      render: (value, row) => {
        const used = row.used_sub_orgs || 0;
        const max = value || 0;
        const percent = max > 0 ? (used / max) * 100 : 0;
        return (
          <Box sx={{ minWidth: 120 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: isDark ? "#ffffff" : "#2a3547" }}>
                {used} / {max}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#cbd5e1" : "#7c8fac" }}>
                {percent.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#e5eaef",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: percent > 80 ? "#fa896b" : "#088164",
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "can_invite_clubs",
      label: "Club Invites",
      width: 170,
      render: (value) => {
        const canInvite = apiFlagIsTrue(value);
        return (
          <Chip
            label={canInvite ? "Can invite clubs" : "Cannot invite clubs"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              backgroundColor: canInvite
                ? (isDark ? "rgba(34, 197, 94, 0.18)" : "rgba(34, 197, 94, 0.1)")
                : (isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(148, 163, 184, 0.12)"),
              color: canInvite
                ? (isDark ? "#4ade80" : "#15803d")
                : (isDark ? "#cbd5e1" : "#64748b"),
            }}
          />
        );
      },
    },
    {
      field: "status",
      label: "Status",
      width: 120,
      align: "right",
      render: (value) => <StatusBadge status={value} />,
    },
  ];

  const renderActions = (row) => (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleOpenEdit(row);
      }}
      sx={{
        color: "#7c8fac",
        "&:hover": { backgroundColor: "#e8f5f1", color: "#088164" },
      }}
    >
      <EditIcon fontSize="small" />
    </IconButton>
  );

  // Green switch styling for permission toggles (matches SubOrganizations)
  const permissionSwitchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: isDark ? "#4ade80" : "#22c55e",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: isDark ? "#4ade80" : "#22c55e",
    },
  };

  const renderPartnerPermissionSwitches = (formik) => (
    <>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={!!formik.values.allow_member_emails}
              onChange={(_event, checked) =>
                formik.setFieldValue("allow_member_emails", checked)
              }
              sx={permissionSwitchSx}
            />
          }
          label="Member details"
          sx={{ color: isDark ? "#cbd5e1" : "#2a3547" }}
        />
        <FormHelperText sx={{ ml: 0, mt: -0.5, color: isDark ? "#94a3b8" : "#7c8fac" }}>
          When off, this partner cannot view sub-org member email addresses in the Members directory.
        </FormHelperText>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={!!formik.values.can_advertise}
              onChange={(_event, checked) =>
                formik.setFieldValue("can_advertise", checked)
              }
              sx={permissionSwitchSx}
            />
          }
          label="Allow advertising"
          sx={{ color: isDark ? "#cbd5e1" : "#2a3547" }}
        />
        <FormHelperText sx={{ ml: 0, mt: -0.5, color: isDark ? "#94a3b8" : "#7c8fac" }}>
          When off, this club partner cannot create or edit advertisements.
        </FormHelperText>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={!!formik.values.can_send_alerts}
              onChange={(_event, checked) =>
                formik.setFieldValue("can_send_alerts", checked)
              }
              sx={permissionSwitchSx}
            />
          }
          label="Allow sending alerts"
          sx={{ color: isDark ? "#cbd5e1" : "#2a3547" }}
        />
        <FormHelperText sx={{ ml: 0, mt: -0.5, color: isDark ? "#94a3b8" : "#7c8fac" }}>
          When off, this club partner cannot send alerts to clubs or members.
        </FormHelperText>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={!!formik.values.can_invite_clubs}
              onChange={(_event, checked) =>
                formik.setFieldValue("can_invite_clubs", checked)
              }
              sx={permissionSwitchSx}
            />
          }
          label="Allow inviting clubs"
          sx={{ color: isDark ? "#cbd5e1" : "#2a3547" }}
        />
        <FormHelperText sx={{ ml: 0, mt: -0.5, color: isDark ? "#94a3b8" : "#7c8fac" }}>
          When off, this club partner cannot create or send club invite codes.
        </FormHelperText>
      </Grid>
    </>
  );

  // Dialog styles (theme-aware)
  const dialogStyles = {
    paper: {
      borderRadius: "16px",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
      backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
    },
    header: {
      p: 3,
      pb: 2,
    },
    title: {
      fontSize: "1.25rem",
      fontWeight: 700,
      color: isDark ? "#ffffff" : "#2a3547",
      mb: 0.5,
    },
    subtitle: {
      fontSize: "0.875rem",
      color: isDark ? "#cbd5e1" : "#5a6a85",
    },
    form: {
      p: 3,
      pt: 2,
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 1.5,
      mt: 3,
    },
  };

  return (
    <Box>
      <PageHeader
        title="Club Partners"
        subtitle="Create and manage club partner accounts"
        breadcrumbs={[{ label: "Admin" }, { label: "Club Partners" }]}
        actionLabel="Create Club Partner"
        actionIcon={<AddIcon />}
        onAction={() => setOpenCreate(true)}
      />

      <Card noPadding>
        <DataTable
          columns={columns}
          data={list}
          loading={loading}
          actions={renderActions}
          emptyMessage="No club partners found"
          pagination={false}
          mobileTitleColumn="organisation_name"
          mobileHiddenColumns={[]}
        />
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={openCreate}
        onClose={() => {
          setOpenCreate(false);
          setCreateProfileImageId(null);
          setCreateProfileImageUrl("");
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogStyles.paper }}
      >
        <Box sx={dialogStyles.header}>
          <Typography sx={dialogStyles.title}>Create Club Partner</Typography>
          <Typography sx={dialogStyles.subtitle}>
            Set up a new club partner. Defaults: advertising on, alerts and member details off.
          </Typography>
        </Box>
        <Divider />
        <Box sx={dialogStyles.form}>
          <form onSubmit={createFormik.handleSubmit}>
            <Grid container spacing={2.5}>
              {/* Profile Image Upload */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar
                    src={createProfileImageUrl}
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: "#e8f5f1",
                      color: "#088164",
                      fontSize: "1.25rem",
                      fontWeight: 600,
                    }}
                  >
                    {createFormik.values.email?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingCreateImage}
                      sx={{ textTransform: 'none' }}
                    >
                      {uploadingCreateImage ? "Uploading..." : "Upload Profile Image"}
                      <input
                        ref={createImageInputRef}
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                        onChange={handleCreateImageUpload}
                      />
                    </Button>
                    {createProfileImageId && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#7c8fac', mt: 0.5 }}>
                        Image uploaded
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Club Name"
                  name="organisation_name"
                  fullWidth
                  size="small"
                  value={createFormik.values.organisation_name}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  error={createFormik.touched.organisation_name && Boolean(createFormik.errors.organisation_name)}
                  helperText={createFormik.touched.organisation_name && createFormik.errors.organisation_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  name="email"
                  fullWidth
                  size="small"
                  value={createFormik.values.email}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  error={createFormik.touched.email && Boolean(createFormik.errors.email)}
                  helperText={createFormik.touched.email && createFormik.errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  fullWidth
                  size="small"
                  value={createFormik.values.password}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  error={createFormik.touched.password && Boolean(createFormik.errors.password)}
                  helperText={createFormik.touched.password && createFormik.errors.password}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Clubs"
                  name="max_sub_orgs"
                  fullWidth
                  size="small"
                  value={createFormik.values.max_sub_orgs}
                  onChange={createFormik.handleChange}
                  onBlur={createFormik.handleBlur}
                  error={createFormik.touched.max_sub_orgs && Boolean(createFormik.errors.max_sub_orgs)}
                  helperText={createFormik.touched.max_sub_orgs && createFormik.errors.max_sub_orgs}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={createFormik.touched.sport_ids && Boolean(createFormik.errors.sport_ids)}
                >
                  <InputLabel id="sport-ids-label">Sports (Optional)</InputLabel>
                  <Select
                    labelId="sport-ids-label"
                    id="sport_ids"
                    name="sport_ids"
                    multiple
                    value={createFormik.values.sport_ids}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    input={<OutlinedInput label="Sports (Optional)" />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: isDark ? '#718096' : '#718096', fontSize: '0.875rem' }}>None selected</Typography>;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const sport = sportsList.find(s => s.id === value);
                            return (
                              <Chip
                                key={value}
                                label={sport?.name || value}
                                size="small"
                                sx={{
                                  backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : 'rgba(25, 118, 210, 0.08)',
                                  color: isDark ? '#818cf8' : '#1976d2',
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                    sx={{
                      backgroundColor: isDark ? '#242424' : '#f7fafc',
                      color: isDark ? '#ffffff' : '#1a202c',
                    }}
                  >
                    {sportsList.map((sport) => (
                      <MenuItem key={sport.id} value={sport.id}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {createFormik.touched.sport_ids && createFormik.errors.sport_ids && (
                    <FormHelperText>{createFormik.errors.sport_ids}</FormHelperText>
                  )}
                  <FormHelperText sx={{ fontSize: '0.75rem', color: isDark ? '#718096' : '#718096', mt: 0.5 }}>
                    Master orgs manage clubs. Sports are optional and can be added later if needed.
                  </FormHelperText>
                </FormControl>
              </Grid>
              {renderPartnerPermissionSwitches(createFormik)}
            </Grid>
            <Box sx={dialogStyles.actions}>
              <ActionButton variant="secondary" onClick={() => {
                setOpenCreate(false);
                setCreateProfileImageId(null);
                setCreateProfileImageUrl("");
              }}>
                Cancel
              </ActionButton>
              <ActionButton type="submit">Create</ActionButton>
            </Box>
          </form>
        </Box>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEditTarget(null);
          setEditProfileImageId(null);
          setEditProfileImageUrl("");
          setEditLoading(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogStyles.paper }}
      >
        <Box sx={dialogStyles.header}>
          <Typography sx={dialogStyles.title}>Edit Club Partner</Typography>
          <Typography sx={dialogStyles.subtitle}>
            Update capacity, status, or profile image for {editTarget?.organisation_name}
          </Typography>
        </Box>
        <Divider />
        <Box sx={dialogStyles.form}>
          {editLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
          <form onSubmit={editFormik.handleSubmit}>
            <Grid container spacing={2.5}>
              {/* Profile Image Upload */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar
                    src={editProfileImageUrl}
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: "#e8f5f1",
                      color: "#088164",
                      fontSize: "1.25rem",
                      fontWeight: 600,
                    }}
                  >
                    {editTarget?.email?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      disabled={uploadingEditImage}
                      sx={{ textTransform: 'none' }}
                    >
                      {uploadingEditImage ? "Uploading..." : "Upload Profile Image"}
                      <input
                        ref={editImageInputRef}
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                        onChange={handleEditImageUpload}
                      />
                    </Button>
                    {(editProfileImageId || editProfileImageUrl) && (
                      <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#7c8fac', mt: 0.5 }}>
                        {editProfileImageId ? 'New image uploaded' : 'Current image'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Clubs"
                  name="max_sub_orgs"
                  fullWidth
                  size="small"
                  type="number"
                  value={editFormik.values.max_sub_orgs}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.max_sub_orgs && Boolean(editFormik.errors.max_sub_orgs)}
                  helperText={editFormik.touched.max_sub_orgs && editFormik.errors.max_sub_orgs}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: isDark ? "#242424" : "#f7fafc",
                      "& fieldset": {
                        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark ? "#6366f1" : "#1976d2",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isDark ? "#818cf8" : "#1976d2",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#cbd5e1" : "#718096",
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : "#1a202c",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Status"
                  name="status"
                  select
                  fullWidth
                  size="small"
                  value={editFormik.values.status}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.status && Boolean(editFormik.errors.status)}
                  helperText={editFormik.touched.status && editFormik.errors.status}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: isDark ? "#242424" : "#f7fafc",
                      "& fieldset": {
                        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark ? "#6366f1" : "#1976d2",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isDark ? "#818cf8" : "#1976d2",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#cbd5e1" : "#718096",
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : "#1a202c",
                    },
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>
              {renderPartnerPermissionSwitches(editFormik)}
            </Grid>
            <Box sx={dialogStyles.actions}>
              <ActionButton variant="secondary" onClick={() => {
                setOpenEdit(false);
                setEditTarget(null);
                setEditProfileImageId(null);
                setEditProfileImageUrl("");
                setEditLoading(false);
              }}>
                Cancel
              </ActionButton>
              <ActionButton type="submit">Save Changes</ActionButton>
            </Box>
          </form>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

export default connect(mapStateToProps)(MasterOrg);
